/**
 * AccionesInstancia (IHC, órdenes f13222da + 74043ee7) — superficie INTERACTIVA same-origin:
 * el HTML del chat deja de ser solo lectura. Para una instancia que el chat-agente consultó:
 *  - ACCIONES: deriva los controles de get_available_actions (verbos filtrados por grant
 *    campo-UUID server-side) y los ejecuta vía la API canónica con el JWT del usuario.
 *  - CAMPOS (andamiaje 74043ee7): cuando el back publique get_available_fields, renderiza los
 *    campos legibles/editables que la tool devuelva (cero hardcode) y los edita vía PATCH
 *    canónico. Mientras la tool no exista, no se muestra nada (graceful).
 *
 * Frontera dual: el cerebro NO actúa; actúa el USUARIO al pulsar/guardar, y el servidor
 * reaplica RBAC+FSM+DMN (no se confía en el front). Sin acciones/campos permitidos → sin
 * control (lo no concedido no se renderiza). Es la PWA same-origin, no el visor sandbox.
 *
 * Estética (operador): sin emojis, sin líneas divisorias; relieve por sombra + fondo;
 * tamaños cómodos (táctil); claro y oscuro vía variables del theme.
 */
import { useEffect, useState, useCallback } from 'react'
import { Loader2, Zap, PencilLine } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { getAvailableActions, getAvailableFields, type AccionesActor, type CamposActor } from '@/lib/mcp'
// f6b8abd7: la ejecución canónica vive en la madre lib/acciones (compartida con el
// broker del visor de informes — HOP-3). Aquí solo queda el chrome (toasts/recarga).
import { rutaCanonica, ejecutarVerboCanonico } from '@/lib/acciones'
import { toastError } from '@/lib/errores'
import { Button } from './ui/button'

interface AccionesInstanciaProps {
  instanciaId: string
  /** VC-1 (1da5c7a4): aviso al contenedor tras un verbo ejecutado con éxito — la vista
   *  que lista la instancia (p.ej. Contratos) recarga su consulta derivada. Opcional:
   *  los consumidores IHC existentes no cambian. */
  onEjecutado?: () => void
  /** F3 Inc 5 (camino B): pide confirmación explícita antes de ejecutar un verbo. Lo activa el
   *  chat de clientes — el asistente PROPONE una escritura, el humano la confirma (gate 1) y el
   *  servidor la revalida. Opt-in: los demás consumidores IHC mantienen el click directo. */
  confirmarAntes?: boolean
}

export function AccionesInstancia({ instanciaId, onEjecutado, confirmarAntes = false }: AccionesInstanciaProps) {
  const token = useAuthStore((s) => s.token)
  const [info, setInfo] = useState<AccionesActor | null>(null)
  const [campos, setCampos] = useState<CamposActor | null>(null)
  const [cargando, setCargando] = useState(true)
  const [ejecutando, setEjecutando] = useState<string | null>(null)
  // F3 Inc 5: verbo pendiente de confirmación (camino B). null = sin propuesta pendiente.
  const [confirmando, setConfirmando] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    // Acciones (existe) + campos (andamiaje: la tool puede no estar aún → null, graceful).
    const [acc, cmp] = await Promise.all([
      getAvailableActions(instanciaId, token).catch(() => null),
      getAvailableFields(instanciaId, token).catch(() => null),
    ])
    setInfo(acc)
    setCampos(cmp)
    setCargando(false)
  }, [instanciaId, token])

  useEffect(() => { cargar() }, [cargar])

  // Camino B: al pulsar un verbo, o pide confirmación (chat) o ejecuta directo (resto).
  function solicitarVerbo(verbo: string) {
    if (ejecutando) return
    if (confirmarAntes) setConfirmando(verbo)
    else ejecutarVerbo(verbo)
  }

  async function ejecutarVerbo(verbo: string) {
    if (!info || ejecutando) return
    setEjecutando(verbo)
    try {
      await ejecutarVerboCanonico(info.entidad, instanciaId, verbo, token, 'ihc')
      toast.success(`${verbo} ejecutado · la instancia cambió de estado`)
      setConfirmando(null)
      await cargar()
      onEjecutado?.()
    } catch (e: any) {
      toastError(e, `${verbo} rechazado`)
    } finally {
      setEjecutando(null)
    }
  }

  if (cargando) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Resolviendo acciones…
      </div>
    )
  }

  const hayAcciones = !!info && info.acciones_disponibles.length > 0
  const hayCampos = !!campos && ((campos.campos_legibles?.length || 0) + (campos.campos_editables?.length || 0) > 0)
  if (!hayAcciones && !hayCampos) return null // lo no concedido no se renderiza (IHC-4)

  const entidadLegible = (info?.entidad || campos?.entidad || '').split('.').slice(1).join('.')

  return (
    <div className="mt-2.5 rounded-xl bg-card p-3.5 shadow-md ring-1 ring-black/5 dark:ring-white/10">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Zap className="h-3.5 w-3.5 text-primary" />
        <span className="font-mono font-medium text-foreground">{entidadLegible}</span>
        {info?.current_state && (
          <>
            <span className="opacity-40">·</span>
            <span>{info.current_state}</span>
          </>
        )}
        <span className="ml-auto font-mono opacity-50">{instanciaId.slice(0, 8)}</span>
      </div>

      {hayAcciones && (
        confirmando ? (
          // Camino B: propuesta de escritura del asistente → confirmación explícita del humano
          // (gate 1). Muestra el verbo REAL del servidor (anti label-spoofing). Sin confirmar, no muta.
          <div className="mt-3 rounded-lg bg-muted/50 p-3">
            <div className="text-xs text-foreground">
              El asistente propone <span className="font-mono font-semibold">{confirmando}</span> sobre{' '}
              <span className="font-mono">{entidadLegible}</span>
              {info?.current_state ? ` (${info.current_state})` : ''}. El servidor revalidará la transición (RBAC + FSM + DMN).
            </div>
            <div className="mt-2.5 flex gap-2">
              <Button size="default" disabled={ejecutando !== null} onClick={() => ejecutarVerbo(confirmando)} className="shadow-sm">
                {ejecutando === confirmando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span className="font-mono">Confirmar {confirmando}</span>
              </Button>
              <Button variant="ghost" size="default" disabled={ejecutando !== null} onClick={() => setConfirmando(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {info!.acciones_disponibles.map((verbo) => (
              <Button
                key={verbo}
                variant="default"
                size="default"
                disabled={ejecutando !== null}
                onClick={() => solicitarVerbo(verbo)}
                className="shadow-sm"
                title={`${confirmarAntes ? 'Proponer' : 'Ejecutar'} ${verbo} (vía API canónica, validado por el servidor)`}
              >
                {ejecutando === verbo ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span className="font-mono">{verbo}</span>
              </Button>
            ))}
          </div>
        )
      )}

      {/* IHC-CAMPOS (74043ee7): la key resetea el estado del editor cuando el servidor
          devuelve valores nuevos (post-guardado/recarga) — sin ella quedarían inputs stale. */}
      {hayCampos && (
        <CamposEditor
          key={(campos!.campos_editables || []).map((c) => `${c.clave}=${aTexto(c.valor)}`).join('|')}
          campos={campos!}
          token={token}
          onGuardado={cargar}
        />
      )}

      <div className="mt-2.5 text-[10px] text-muted-foreground">
        Solo se muestran las acciones y campos que tu permiso autoriza; el servidor los revalida (RBAC + FSM + DMN).
      </div>
    </div>
  )
}

/** Texto inicial del input desde el valor crudo de la tool (objetos → JSON legible). */
function aTexto(valor: unknown): string {
  if (valor == null) return ''
  if (typeof valor === 'object') { try { return JSON.stringify(valor, null, 2) } catch { return String(valor) } }
  return String(valor)
}

/** Coerciona el texto del input al DATATYPE declarado del campo antes del PATCH.
 *  Devuelve { ok, valor } — `ok:false` corta el guardado con aviso (no se envía basura). */
function coercionarPorTipo(texto: string, tipo?: string): { ok: true; valor: unknown } | { ok: false; motivo: string } {
  const t = (tipo || 'String').toLowerCase()
  if (t === 'number') {
    if (texto.trim() === '') return { ok: true, valor: null }
    const n = Number(texto)
    return Number.isFinite(n) ? { ok: true, valor: n } : { ok: false, motivo: 'no es un número' }
  }
  if (t === 'boolean') {
    if (texto === 'true') return { ok: true, valor: true }
    if (texto === 'false') return { ok: true, valor: false }
    return { ok: false, motivo: 'debe ser true o false' }
  }
  if (t === 'json' || t === 'array') {
    if (texto.trim() === '') return { ok: true, valor: null }
    try { return { ok: true, valor: JSON.parse(texto) } } catch { return { ok: false, motivo: 'JSON inválido' } }
  }
  return { ok: true, valor: texto } // String/Text/Email/URL/UUID/Enum/Markdown/Timestamp/… → string
}

const TIPOS_MULTILINEA = new Set(['text', 'markdown', 'json', 'array'])

/**
 * Editor de campos (IHC-CAMPOS, 74043ee7): pinta tal cual lo que get_available_fields
 * devuelva — campos_legibles (solo lectura) y campos_editables (input + guardar vía PATCH
 * canónico). Cero hardcode de permisos: si la tool no lista un campo como editable, no se
 * edita. El PATCH envía SOLO los campos modificados (merge semantics del endpoint; no se
 * pisa _provenance de lo no tocado) y coerciona cada valor por su datatype declarado.
 */
function CamposEditor({ campos, token, onGuardado }: { campos: CamposActor; token: string | null; onGuardado: () => void }) {
  const legibles = campos.campos_legibles || []
  const editables = campos.campos_editables || []
  const iniciales = Object.fromEntries(editables.map((c) => [c.clave, aTexto(c.valor)]))
  const [valores, setValores] = useState<Record<string, string>>(() => ({ ...iniciales }))
  const [guardando, setGuardando] = useState(false)

  const r = rutaCanonica(campos.entidad)
  const sucios = editables.filter((c) => (valores[c.clave] ?? '') !== (iniciales[c.clave] ?? ''))

  async function guardar() {
    if (!r || guardando || sucios.length === 0) return
    // Solo campos modificados, coercionados por su tipo declarado.
    const payload: Record<string, unknown> = {}
    for (const c of sucios) {
      const res = coercionarPorTipo(valores[c.clave] ?? '', c.tipo)
      if (!res.ok) { toast.error(`${c.clave}: ${res.motivo}`); return }
      payload[c.clave] = res.valor
    }
    setGuardando(true)
    try {
      // Edición gobernada: PATCH canónico con el JWT; el enforce reaplica RBAC campo-UUID + DMN.
      // v1.0 = semilla; el routing version-agnóstico (lib/api CUI-2) la reescribe a la viva.
      await api.patch(
        `/canonico/${r.sub}/${r.tk}/${r.estado}/v1.0/instancias/${campos.instancia_id}`,
        token,
        { payload },
      )
      toast.success(`${sucios.length} campo(s) guardado(s) · validado por el servidor`)
      onGuardado()
    } catch (e: any) {
      toastError(e, 'Edición rechazada')
    } finally {
      setGuardando(false)
    }
  }

  const estiloInput =
    'mt-1 w-full rounded-lg bg-background px-3 py-2 text-sm shadow-sm ring-1 ring-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:ring-white/10'

  return (
    <div className="mt-3 space-y-2.5">
      {legibles.length > 0 && (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {legibles.map((c) => (
            <div key={c.clave} className="rounded-lg bg-muted/50 px-2.5 py-1.5">
              <div className="font-mono text-[11px] text-muted-foreground">{c.clave}</div>
              <div className="text-sm break-words">{c.valor == null || c.valor === '' ? '—' : aTexto(c.valor)}</div>
            </div>
          ))}
        </div>
      )}
      {editables.length > 0 && (
        <div className="space-y-2">
          {editables.map((c) => {
            const t = (c.tipo || 'String').toLowerCase()
            return (
              <label key={c.clave} className="block">
                <span className="font-mono text-[11px] text-muted-foreground">
                  {c.clave}
                  {c.tipo ? <span className="opacity-50"> · {c.tipo}</span> : null}
                </span>
                {t === 'boolean' ? (
                  <select
                    value={valores[c.clave] ?? ''}
                    onChange={(e) => setValores((v) => ({ ...v, [c.clave]: e.target.value }))}
                    className={estiloInput}
                  >
                    <option value="">—</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : TIPOS_MULTILINEA.has(t) ? (
                  <textarea
                    value={valores[c.clave] ?? ''}
                    rows={3}
                    onChange={(e) => setValores((v) => ({ ...v, [c.clave]: e.target.value }))}
                    className={`${estiloInput} font-mono text-xs`}
                  />
                ) : (
                  <input
                    value={valores[c.clave] ?? ''}
                    inputMode={t === 'number' ? 'decimal' : undefined}
                    onChange={(e) => setValores((v) => ({ ...v, [c.clave]: e.target.value }))}
                    className={estiloInput}
                  />
                )}
              </label>
            )
          })}
          <Button size="default" disabled={guardando || sucios.length === 0} onClick={guardar} className="shadow-sm">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <PencilLine className="h-4 w-4" />}
            {sucios.length === 0 ? 'Sin cambios' : `Guardar ${sucios.length} cambio(s)`}
          </Button>
        </div>
      )}
    </div>
  )
}
